import { ApiError, apiRequest } from './client';
import { validateChatAttachment, validateVoiceMessage } from '../utils/fileValidation';
import {
  mockAssignChatAdmins,
  mockGetChat,
  mockGetMessageWindow,
  mockListChats,
  mockMarkChatRead,
  mockMarkChatUnread,
  mockSearchChat,
  mockSendChatMessage,
  mockSendFileMessage,
  mockSendVoiceMessage,
} from '../mocks/chats';
import type { components } from './generated/schema';
import type {
  AdminChatListItem,
  ChatAdminSummary,
  ChatAttachment,
  ChatMessage,
  ChatMessageWindow,
  ChatPageMeta,
  ChatSearchResult,
  PatientChatThread,
} from '../types/admin';

type SendChatMessageRequest = {
  readonly message_text: string;
};

type EditChatMessageRequest = {
  readonly message_text: string;
};

export type SendFileMessageRequest = {
  readonly file: File;
  readonly filename?: string;
};

export type SendVoiceMessageRequest = {
  readonly file: Blob;
  readonly duration_ms: number;
  readonly waveform?: readonly number[];
  readonly filename?: string;
};

type AssignChatAdminsRequest = {
  readonly admin_ids: readonly string[];
};

export type ApiChatParticipantSummary = components['schemas']['ChatParticipantSummary'];
export type ApiChatSenderSummary = components['schemas']['ChatSenderSummary'];
export type ApiChatAttachment = components['schemas']['ChatAttachmentItem'];
export type ApiChatMessage = components['schemas']['ChatMessageItem'];
export type ApiPatientChatThread = components['schemas']['PatientChatThread'];
export type ApiAdminChatListItem = components['schemas']['AdminChatListItem'];
type ApiAdminChatListResponse = components['schemas']['AdminChatListResponse'];

// Hand-typed until the OpenAPI schema is regenerated for the new endpoints.
type ApiChatPageMeta = {
  readonly has_more_before?: boolean;
  readonly has_more_after?: boolean;
  readonly oldest_cursor?: string | null;
  readonly newest_cursor?: string | null;
  readonly page_size?: number;
};

type ApiChatMessagesPage = {
  readonly messages: readonly ApiChatMessage[];
  readonly page?: ApiChatPageMeta;
};

type ApiChatSearchHit = {
  readonly id: string;
  readonly created_at: string;
  readonly snippet: string;
  readonly sender_role: string;
  readonly sender_display_name: string;
};

type ApiChatSearchResponse = {
  readonly hits: readonly ApiChatSearchHit[];
  readonly total: number;
  readonly next_cursor: string | null;
  readonly query: string;
};

export type MessageWindowOptions = {
  readonly before?: string;
  readonly after?: string;
  readonly around?: string;
  /** ISO-8601 instant; loads the window around the first message on/after it. */
  readonly around_date?: string;
  readonly limit?: number;
};

const attachmentFilenameByUrl = new Map<string, string>();

function shouldFallback(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return true;
  }
  return error.status === 404 || error.status === 405 || error.status === 501;
}

export async function getChats(accessToken: string, search?: string): Promise<readonly AdminChatListItem[]> {
  try {
    const qs = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const response = await apiRequest<ApiAdminChatListResponse>(`/admin/chats${qs}`, {
      token: accessToken,
    });
    return response.items.map(normalizeAdminChatListItem);
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockListChats();
  }
}

export async function getPatientChat(
  patientId: string,
  accessToken: string,
): Promise<PatientChatThread | null> {
  try {
    const response = await apiRequest<ApiPatientChatThread>(
      `/admin/patients/${patientId}/chat`,
      { token: accessToken },
    );
    return normalizePatientChatThread(response);
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockGetChat(patientId);
  }
}

/** Loads one keyset page of messages. Pass at most one of before/after/around. */
export async function getMessageWindow(
  patientId: string,
  accessToken: string,
  options: MessageWindowOptions = {},
): Promise<ChatMessageWindow> {
  const params = new URLSearchParams();
  if (options.before) {
    params.set('before', options.before);
  }
  if (options.after) {
    params.set('after', options.after);
  }
  if (options.around) {
    params.set('around', options.around);
  }
  if (options.around_date) {
    params.set('around_date', options.around_date);
  }
  params.set('limit', String(options.limit ?? 30));
  try {
    const response = await apiRequest<ApiChatMessagesPage>(
      `/admin/patients/${patientId}/chat/messages?${params.toString()}`,
      { token: accessToken },
    );
    return {
      messages: response.messages.map((message) => normalizeChatMessage(message, patientId)),
      page: normalizeChatPageMeta(response.page),
    };
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockGetMessageWindow(patientId, options);
  }
}

/** Searches the whole chat history for text messages matching [query]. */
export async function searchPatientChat(
  patientId: string,
  accessToken: string,
  options: { readonly q: string; readonly limit?: number; readonly before?: string },
): Promise<ChatSearchResult> {
  const params = new URLSearchParams({ q: options.q });
  params.set('limit', String(options.limit ?? 30));
  if (options.before) {
    params.set('before', options.before);
  }
  try {
    const response = await apiRequest<ApiChatSearchResponse>(
      `/admin/patients/${patientId}/chat/search?${params.toString()}`,
      { token: accessToken },
    );
    return {
      hits: response.hits,
      total: response.total,
      next_cursor: response.next_cursor ?? null,
      query: response.query,
    };
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockSearchChat(patientId, options.q);
  }
}

export async function sendChatMessage(
  patientId: string,
  request: SendChatMessageRequest,
  accessToken: string,
  currentUser: { readonly full_name: string; readonly id: string },
): Promise<ChatMessage> {
  try {
    await apiRequest<void>(`/admin/patients/${patientId}/chat/messages`, {
      method: 'POST',
      body: request,
      token: accessToken,
    });
    return {
      id: `tmp-${Date.now()}`,
      patient_id: patientId,
      sender_user_id: currentUser.id,
      sender_role: 'admin',
      sender_name: currentUser.full_name,
      sender_avatar_url: null,
      message_text: request.message_text,
      message_type: 'text',
      created_at: new Date().toISOString(),
      is_edited: false,
      attachments: [],
    };
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockSendChatMessage(patientId, request.message_text, currentUser.full_name, currentUser.id);
  }
}

export async function sendFileMessage(
  patientId: string,
  request: SendFileMessageRequest,
  accessToken: string,
  currentUser: { readonly full_name: string; readonly id: string },
): Promise<ChatMessage> {
  const validationError = validateChatAttachment(request.file);
  if (validationError === 'type') {
    throw new ApiError('Unsupported file type.', 415);
  }
  if (validationError === 'size') {
    throw new ApiError('File exceeds the 20 MB size limit.', 413);
  }

  const formData = new FormData();
  formData.set('file', request.file, request.filename ?? request.file.name ?? 'chat-attachment');

  try {
    const response = await apiRequest<ApiChatMessage>(
      `/admin/patients/${patientId}/chat/messages/file`,
      {
        method: 'POST',
        body: formData,
        token: accessToken,
      },
    );
    cacheAttachmentFilenames(response.attachments, request.filename ?? request.file.name ?? null);
    return normalizeChatMessage(response, patientId);
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockSendFileMessage(
      patientId,
      request.file,
      currentUser.full_name,
      currentUser.id,
      request.filename ?? request.file.name ?? null,
    );
  }
}

export async function sendVoiceMessage(
  patientId: string,
  request: SendVoiceMessageRequest,
  accessToken: string,
  currentUser: { readonly full_name: string; readonly id: string },
): Promise<ChatMessage> {
  const validationError = validateVoiceMessage(request.file);
  if (validationError === 'size') {
    throw new ApiError('Voice message exceeds the 20 MB size limit.', 413);
  }

  const formData = new FormData();
  formData.set('file', request.file, request.filename ?? 'voice-message.webm');
  formData.set('duration_ms', String(request.duration_ms));
  if (request.waveform && request.waveform.length > 0) {
    formData.set('waveform', JSON.stringify(request.waveform));
  }

  try {
    const response = await apiRequest<ApiChatMessage>(
      `/admin/patients/${patientId}/chat/messages/voice`,
      {
        method: 'POST',
        body: formData,
        token: accessToken,
      },
    );
    cacheAttachmentFilenames(response.attachments, request.filename ?? null);
    return normalizeChatMessage(response, patientId);
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockSendVoiceMessage(
      patientId,
      request.duration_ms,
      currentUser.full_name,
      currentUser.id,
    );
  }
}

export async function editChatMessage(
  patientId: string,
  messageId: string,
  request: EditChatMessageRequest,
  accessToken: string,
): Promise<ChatMessage> {
  const response = await apiRequest<ApiChatMessage>(
    `/admin/patients/${patientId}/chat/messages/${messageId}`,
    {
      method: 'PATCH',
      body: request,
      token: accessToken,
    },
  );
  return normalizeChatMessage(response, patientId);
}

export async function deleteChatMessage(
  patientId: string,
  messageId: string,
  accessToken: string,
): Promise<void> {
  await apiRequest<void>(
    `/admin/patients/${patientId}/chat/messages/${messageId}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  );
}

export async function setChatReaction(
  patientId: string,
  messageId: string,
  emoji: string,
  accessToken: string,
): Promise<ChatMessage> {
  const response = await apiRequest<ApiChatMessage>(
    `/admin/patients/${encodeURIComponent(patientId)}/chat/messages/${encodeURIComponent(messageId)}/reactions`,
    { method: 'PUT', body: { emoji }, token: accessToken },
  );
  return normalizeChatMessage(response, patientId);
}

export async function removeChatReaction(
  patientId: string,
  messageId: string,
  emoji: string,
  accessToken: string,
): Promise<ChatMessage> {
  const response = await apiRequest<ApiChatMessage>(
    `/admin/patients/${encodeURIComponent(patientId)}/chat/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(emoji)}`,
    { method: 'DELETE', token: accessToken },
  );
  return normalizeChatMessage(response, patientId);
}

export async function markChatRead(patientId: string, accessToken: string): Promise<void> {
  try {
    await apiRequest<void>(`/admin/patients/${patientId}/chat/read`, {
      method: 'POST',
      body: {},
      token: accessToken,
    });
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    await mockMarkChatRead(patientId);
  }
}

export async function assignChatAdmins(
  patientId: string,
  request: AssignChatAdminsRequest,
  accessToken: string,
): Promise<readonly ChatAdminSummary[]> {
  try {
    const response = await apiRequest<readonly ApiChatParticipantSummary[]>(
      `/admin/patients/${patientId}/chat/assignments`,
      {
        method: 'PUT',
        body: request,
        token: accessToken,
      },
    );
    return response.map(normalizeAdminSummary);
  } catch (error) {
    if (!shouldFallback(error)) {
      throw error;
    }
    return mockAssignChatAdmins(patientId, request.admin_ids);
  }
}

type AdminRosterApiItem = {
  readonly id: string;
  readonly admin_id: string;
  readonly full_name: string;
  readonly first_name?: string | null;
  readonly last_name?: string | null;
  readonly is_active?: boolean;
  readonly avatar_url?: string | null;
};

type AdminRosterApiResponse = {
  readonly items: readonly AdminRosterApiItem[];
};

const ADMIN_PALETTE = ['#163b1e', '#407374', '#68772b', '#234748'];

export async function getAdminRoster(
  accessToken: string,
): Promise<readonly ChatAdminSummary[]> {
  const response = await apiRequest<AdminRosterApiResponse>('/admin/admins', {
    token: accessToken,
  });
  return response.items.map((item) => {
    const initials = item.full_name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
    const hash = item.id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return {
      admin_id: item.admin_id,
      user_id: item.id,
      full_name: item.full_name,
      initials: initials || 'WF',
      accent_color: ADMIN_PALETTE[hash % ADMIN_PALETTE.length]!,
      is_online: item.is_active ?? true,
      avatar_url: item.avatar_url ?? null,
    };
  });
}

export function normalizeAdminSummary(
  admin: ApiChatParticipantSummary | ChatAdminSummary,
): ChatAdminSummary {
  if ('full_name' in admin) {
    return admin;
  }

  const initials = admin.display_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  const palette = ['#163b1e', '#407374', '#68772b', '#234748'];
  const hash = admin.admin_id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  // `avatar_url` is being added to ChatParticipantSummary in parallel — read it
  // defensively until the generated schema picks up the new field.
  const extendedAdmin = admin as ApiChatParticipantSummary & {
    readonly avatar_url?: string | null;
  };

  return {
    admin_id: admin.admin_id,
    user_id: admin.user_id,
    full_name: admin.display_name,
    initials: initials || 'WF',
    accent_color: palette[hash % palette.length]!,
    is_online: true,
    avatar_url: extendedAdmin.avatar_url ?? null,
  };
}

export function normalizeAdminChatListItem(item: ApiAdminChatListItem): AdminChatListItem {
  const extended = item as ApiAdminChatListItem & { readonly manually_unread?: boolean };
  return {
    patient_id: item.patient_id,
    patient_name: item.patient_name,
    patient_display_id: item.patient_display_id ?? null,
    patient_avatar_url: item.patient_avatar_url ?? null,
    assigned_admins: item.assigned_admins.map(normalizeAdminSummary),
    last_message_preview: item.last_message_preview ?? 'No messages yet',
    last_message_at: item.last_message_at ?? new Date().toISOString(),
    unread_count: item.unread_count,
    manually_unread: extended.manually_unread ?? false,
  };
}

function normalizeChatPageMeta(meta: ApiChatPageMeta | undefined): ChatPageMeta {
  return {
    has_more_before: meta?.has_more_before ?? false,
    has_more_after: meta?.has_more_after ?? false,
    oldest_cursor: meta?.oldest_cursor ?? null,
    newest_cursor: meta?.newest_cursor ?? null,
    page_size: meta?.page_size ?? 30,
  };
}

export function normalizePatientChatThread(response: ApiPatientChatThread): PatientChatThread {
  // `page` / `last_message_preview` arrive from the paginated backend but are
  // not yet in the generated schema — read them defensively.
  const extended = response as ApiPatientChatThread & {
    readonly page?: ApiChatPageMeta;
    readonly last_message_preview?: string | null;
    readonly manually_unread?: boolean;
  };
  return {
    patient_id: response.patient_id,
    patient_name: response.patient_name,
    patient_display_id: response.patient_display_id ?? null,
    patient_avatar_url: response.patient_avatar_url ?? null,
    assigned_admins: response.assigned_admins.map(normalizeAdminSummary),
    messages: response.messages.map((message) => normalizeChatMessage(message, response.patient_id)),
    unread_count: response.unread_count,
    manually_unread: extended.manually_unread ?? false,
    last_message_at: response.last_message_at ?? null,
    last_message_preview: extended.last_message_preview ?? null,
    page: normalizeChatPageMeta(extended.page),
  };
}

export function normalizeChatMessage(message: ApiChatMessage, patientId: string): ChatMessage {
  const extended = message as ApiChatMessage & {
    readonly reactions?: readonly { readonly emoji: string; readonly count: number; readonly reacted_by_me: boolean }[];
  };
  return {
    id: message.id,
    patient_id: patientId,
    sender_user_id: message.sender.user_id,
    sender_role: normalizeSenderRole(message.sender.role),
    sender_name: message.sender.display_name,
    sender_avatar_url: message.sender.avatar_url ?? null,
    message_text: message.message_text,
    message_type: normalizeMessageType(message.message_type),
    created_at: message.created_at,
    is_edited: message.is_edited,
    attachments: (message.attachments ?? []).map(normalizeChatAttachment),
    reactions: extended.reactions ?? [],
  };
}

export async function markChatUnread(patientId: string, accessToken: string): Promise<void> {
  try {
    await apiRequest<void>(`/admin/patients/${encodeURIComponent(patientId)}/chat/unread`, {
      method: 'POST',
      token: accessToken,
    });
  } catch (error) {
    if (!shouldFallback(error)) throw error;
    await mockMarkChatUnread(patientId);
  }
}

function normalizeChatAttachment(attachment: ApiChatAttachment): ChatAttachment {
  const extendedAttachment = attachment as ApiChatAttachment & {
    readonly original_filename?: string | null;
  };
  return {
    id: attachment.id,
    attachment_type: attachment.attachment_type,
    content_url: attachment.content_url,
    original_filename:
      extendedAttachment.original_filename ?? attachmentFilenameByUrl.get(attachment.content_url) ?? null,
    mime_type: attachment.mime_type ?? null,
    byte_size: attachment.byte_size ?? null,
    duration_ms: attachment.duration_ms ?? null,
    waveform: attachment.waveform ?? null,
  };
}

function normalizeSenderRole(value: string): ChatMessage['sender_role'] {
  return value === 'patient' ? 'patient' : 'admin';
}

function normalizeMessageType(value: string): ChatMessage['message_type'] {
  if (value === 'voice' || value === 'file') {
    return value;
  }
  return 'text';
}

function cacheAttachmentFilenames(
  attachments: readonly ApiChatAttachment[] | undefined,
  filename: string | null,
) {
  if (!attachments || !filename) {
    return;
  }
  for (const attachment of attachments) {
    attachmentFilenameByUrl.set(attachment.content_url, filename);
  }
}
